import classNames from 'classnames';
import { ComponentProps, ReactNode } from 'react';
import GoogleLogin from 'react-google-login';
import { FormattedMessage } from 'react-intl';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import { post } from '@alltrails/core/utils/post';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import { useModalAriaLabelledBy } from '@alltrails/modal';
import GoogleNoBackground from '@alltrails/denali/icons/GoogleNoBackground';
import AppPromotion from '@alltrails/install-prompt/components/AppPromotion';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import AltLoginsForm from '../AltLoginsForm';
import altLoginStyles from '../AltLoginsForm/AltLoginsForm.module.scss';
import SectionDivider from '../SectionDivider';
import TermsOfService from '../TermsOfService';
import ContinueInAppButton from './ContinueInAppButton';
import styles from './InitialMobile.module.scss';
import signupModalStyles from './SignUpModal.module.scss';

type InitialMobileSimplifiedProps = Pick<ComponentProps<typeof AltLoginsForm>, 'googleLoginHandler'> & {
  heading?: ReactNode;
  onMoreOptionsClick: () => void;
  deeplinkData?: DeeplinkData;
  displayTrigger: CarouselDisplayTrigger;
  hideAppDetails: boolean;
  hideAppPromotion?: boolean;
};

export default function InitialMobileSimplified({
  heading,
  onMoreOptionsClick,
  deeplinkData,
  displayTrigger,
  googleLoginHandler,
  hideAppDetails,
  hideAppPromotion = false
}: InitialMobileSimplifiedProps) {
  const ariaLabelledBy = useModalAriaLabelledBy();
  // PLAT-6505
  // const experiments = useExperiments();
  const isUsingNewGoogleLoginButton = false; //experiments?.['grow-rm-google-auth']?.value === 'treatment';

  return (
    <div className={styles.content}>
      {hideAppPromotion ? (
        <Heading as="h4" id={ariaLabelledBy} size="400">
          {heading}
        </Heading>
      ) : (
        <AppPromotion heading={heading} hideDetails={hideAppDetails} />
      )}
      <div>
        {!hideAppPromotion && (
          <>
            <ContinueInAppButton flatColors deeplinkData={deeplinkData} displayTrigger={displayTrigger} />
            {!hideAppDetails ? (
              <SectionDivider classes={signupModalStyles.divider}>
                <FormattedMessage defaultMessage="or" />
              </SectionDivider>
            ) : (
              <div className={styles.spacer} />
            )}
          </>
        )}

        {/* Google Login Button */}
        <div className={altLoginStyles.content}>
          {isUsingNewGoogleLoginButton ? (
            <button
              className={classNames(altLoginStyles.google, altLoginStyles.flatColors)}
              onClick={() => {
                logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'google' });

                const client = window.google.accounts.oauth2.initCodeClient({
                  client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
                  scope: 'openid',
                  callback: async ({ code }) => {
                    const response = await post<{ token: string }>('/api/mugen/google/code', {
                      code
                    });

                    googleLoginHandler(response.token);
                  }
                });

                client.requestCode();
              }}
              type="button"
            >
              <GoogleNoBackground className={altLoginStyles.icon} />
              <span className={altLoginStyles.text}>
                <FormattedMessage defaultMessage="Continue with Google" />
              </span>
            </button>
          ) : (
            <GoogleLogin
              clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}
              render={renderProps => (
                <button
                  className={classNames(altLoginStyles.google, altLoginStyles.flatColors)}
                  onClick={() => {
                    logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'google' });
                    renderProps.onClick();
                  }}
                  type="button"
                >
                  <GoogleNoBackground className={altLoginStyles.icon} />
                  <span className={altLoginStyles.text}>
                    <FormattedMessage defaultMessage="Continue with Google" />
                  </span>
                </button>
              )}
              onSuccess={googleLoginHandler}
              cookiePolicy="single_host_origin"
            />
          )}
        </div>

        {/* More Options Button */}
        <Button
          className={signupModalStyles.email}
          onClick={onMoreOptionsClick}
          text={<FormattedMessage defaultMessage="More options" />}
          width="full"
        />
      </div>

      <div className={styles.already}>
        <TermsOfService />
      </div>
    </div>
  );
}
